import * as yup from "yup";
import { createAddressFields } from "../constants";
import { type FormSectionConfig, type TaxFormConfig, type SelectOption } from "../types";

export const W8IMY_CHAPTER_3_STATUS_OPTIONS: SelectOption[] = [
  { value: "qualified_intermediary_qi_or_qdd", label: "Qualified intermediary (QI or QDD)" },
  { value: "nonqualified_intermediary", label: "Nonqualified intermediary" },
  { value: "territory_financial_institution", label: "Territory financial institution" },
  { value: "us_branch", label: "U.S. branch" },
  { value: "withholding_foreign_partnership", label: "Withholding foreign partnership" },
  { value: "withholding_foreign_trust", label: "Withholding foreign trust" },
  { value: "nonwithholding_foreign_partnership", label: "Nonwithholding foreign partnership" },
  {
    value: "nonwithholding_foreign_simple_trust",
    label: "Nonwithholding foreign simple trust",
  },
  {
    value: "nonwithholding_foreign_grantor_trust",
    label: "Nonwithholding foreign grantor trust",
  },
];

export const W8IMY_CHAPTER_4_STATUS_OPTIONS: SelectOption[] = [
  {
    value: "nonparticipating_foreign_financial_institution",
    label: "Nonparticipating foreign financial institution",
  },
  { value: "participating_ffi", label: "Participating FFI" },
  { value: "reporting_model_1_ffi", label: "Reporting Model 1 FFI" },
  { value: "reporting_model_2_ffi", label: "Reporting Model 2 FFI" },
  { value: "registered_deemed_compliant_ffi", label: "Registered deemed-compliant FFI" },
  { value: "territory_financial_institution", label: "Territory financial institution" },
  { value: "sponsored_ffi", label: "Sponsored FFI" },
  {
    value: "cert_dc_nonreg_local_bank",
    label: "Certified deemed-compliant nonregistering local bank",
  },
  {
    value: "cert_dc_ffi_low_value_accounts",
    label: "Certified deemed-compliant FFI with only low-value accounts",
  },
  {
    value: "cert_dc_sponsored_closely_held_inv_vehicle",
    label: "Certified deemed-compliant sponsored, closely held investment vehicle",
  },
  {
    value: "cert_dc_limited_life_debt_inv_entity",
    label: "Certified deemed-compliant limited life debt investment entity",
  },
  {
    value: "certain_inv_entities_no_financial_accounts",
    label: "Certain investment entities that do not maintain financial accounts",
  },
  { value: "owner_documented_ffi", label: "Owner-documented FFI" },
  { value: "restricted_distributor", label: "Restricted distributor" },
  { value: "foreign_central_bank_of_issue", label: "Foreign central bank of issue" },
  { value: "nonreporting_iga_ffi", label: "Nonreporting IGA FFI" },
  { value: "exempt_retirement_plans", label: "Exempt retirement plans" },
  { value: "excepted_nonfinancial_group_entity", label: "Excepted nonfinancial group entity" },
  {
    value: "excepted_nonfinancial_start_up_company",
    label: "Excepted nonfinancial start-up company",
  },
  {
    value: "excepted_nonfinancial_entity_liquidation_bankruptcy",
    label: "Excepted nonfinancial entity in liquidation or bankruptcy",
  },
  {
    value: "publicly_traded_nffe_or_affiliate",
    label: "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  },
  { value: "excepted_territory_nffe", label: "Excepted territory NFFE" },
  { value: "active_nffe", label: "Active NFFE" },
  { value: "passive_nffe", label: "Passive NFFE" },
  { value: "direct_reporting_nffe", label: "Direct reporting NFFE" },
  { value: "sponsored_direct_reporting_nffe", label: "Sponsored direct reporting NFFE" },
];

export const W8IMY_US_TIN_TYPE_OPTIONS: SelectOption[] = [
  { value: "qi_ein", label: "QI-EIN" },
  { value: "wp_ein", label: "WP-EIN" },
  { value: "wt_ein", label: "WT-EIN" },
  { value: "ein", label: "EIN" },
];

const sections: FormSectionConfig[] = [
  {
    id: "identification",
    title: "Identification of Entity",
    fields: [
      {
        name: "entityName",
        type: "text",
        label: "Entity name",
        placeholder: "",
        required: true,
        gridColumn: "half",
      },
      {
        name: "countryOfIncorporation",
        type: "country",
        label: "Country of incorporation",
        placeholder: "Select a country",
        required: true,
        gridColumn: "half",
      },
      {
        name: "disregardedEntityName",
        type: "text",
        label: "Disregarded entity name",
        gridColumn: "full",
      },
      {
        name: "chapter3Status",
        type: "select",
        label: "Chapter 3 status",
        placeholder: "Select a status",
        required: true,
        gridColumn: "full",
        options: W8IMY_CHAPTER_3_STATUS_OPTIONS,
      },
      {
        name: "chapter4Status",
        type: "select",
        label: "Chapter 4 status (FATCA)",
        placeholder: "Select a status",
        required: true,
        gridColumn: "full",
        options: W8IMY_CHAPTER_4_STATUS_OPTIONS,
      },
      {
        name: "_permanentAddressLabel",
        type: "label",
        label: "Permanent residence address",
        required: true,
        linkedField: "permanentAddress",
        gridColumn: "full",
      },
      ...createAddressFields("permanentAddress", {
        helpText:
          "Do not use a P.O. box or in-care-of address (other than a registered address)",
      }),
      {
        name: "usTinType",
        type: "select",
        label: "US TIN type",
        placeholder: "Select a type",
        gridColumn: "half",
        options: W8IMY_US_TIN_TYPE_OPTIONS,
        plainLabel: true,
      },
      {
        name: "usTin",
        type: "text",
        label: "US TIN",
        gridColumn: "half",
      },
      {
        name: "giin",
        type: "giin",
        label: "GIIN",
        placeholder: "XXXXXX-XXXXX-XX-XXX",
        tooltip: {
          content:
            "GIIN is a 19-character identifier (including dots) and cannot contain the letter 'O'.",
        },
        gridColumn: "half",
      },
      {
        name: "foreignTin",
        type: "text",
        label: "Foreign TIN",
        gridColumn: "half",
      },
      {
        name: "giinVerified",
        type: "checkbox",
        label: "Check if GIIN has been verified through the IRS portal",
        gridColumn: "full",
      },
    ],
  },
  {
    id: "sponsor",
    title: "Sponsor",
    fields: [
      {
        name: "sponsorName",
        type: "text",
        label: "Sponsor name",
        gridColumn: "half",
      },
    ],
  },
];

const validationSchema = yup.object().shape({
  entityName: yup.string().required("Entity name is required"),
  countryOfIncorporation: yup.string().required("Country of incorporation is required"),
  chapter3Status: yup.string().required("Chapter 3 status is required"),
  chapter4Status: yup.string().required("Chapter 4 status (FATCA) is required"),
  permanentAddress: yup.object().shape({
    country: yup.string().required("Country is required"),
    streetLine1: yup.string().required("Street (line 1) is required"),
    city: yup.string().required("City is required"),
    zipCode: yup.string().required("Zip code is required"),
  }),
  giinVerified: yup.boolean().when("giin", {
    is: (giin: string) => Boolean(giin?.trim()),
    then: (schema) =>
      schema.test("giin-validation", "", function (value) {
        const { giin } = this.parent as { giin: string };
        if (giin?.trim() && giin.replace(/\./g, "").length !== 16) {
          return this.createError({ message: "GIIN provided is incomplete" });
        }
        if (value !== true) {
          return this.createError({
            message: "GIIN provided must be verified through the IRS portal",
          });
        }
        return true;
      }),
  }),
});

const initialValues = {
  entityName: "",
  countryOfIncorporation: "",
  disregardedEntityName: "",
  chapter3Status: "",
  chapter4Status: "",
  permanentAddress: {
    country: "",
    streetLine1: "",
    streetLine2: "",
    city: "",
    stateOrProvince: "",
    zipCode: "",
  },
  usTinType: "",
  usTin: "",
  giin: "",
  foreignTin: "",
  giinVerified: false,
  sponsorName: "",
};

export const w8imyConfig: TaxFormConfig = {
  formId: "W8IMY",
  title: "New FATCA details",
  subtitle: "W-8IMY",
  sections,
  validationSchema,
  initialValues,
};
